// docRoute.mjs — pure helpers for document routing (SB-029 第二片：文档间跳转).
//
// WHY a separate module: the publish pipeline bakes `/docs/<group>/<docKey>` into
// every in-document link (publish-pipeline.md §4, `class="doc-link"`), so the href
// IS the route. Parsing/【building】that route is pure string work — keeping it out of
// the JSX is what makes it unit-testable without a DOM.
//
// docKey is a multi-segment logical path (B13), e.g.
//   documents/objectives/product-spec-constraints/ideal-product-spec/00-foundations/architecture-principles
// so the route must be a SPLAT (`/docs/:group/*`), never a single `:param`.

const PREFIX = '/docs/';

// Build the canonical route for a document. Segments are left un-encoded so the
// URL stays readable (docKeys are ASCII by construction — the en physical path).
export function docHref(group, docKey) {
  return `${PREFIX}${group}/${docKey}`;
}

// Parse an in-document href into {group, docKey, frag} — or null when it is not a
// document route (external, asset, folder link, bare anchor…). Tolerates percent-
// encoding (the browser may encode when the href round-trips through the address bar).
export function parseDocHref(href) {
  if (typeof href !== 'string' || !href.startsWith(PREFIX)) return null;
  const [pathPart, ...fragParts] = href.split('#');
  const frag = fragParts.length ? fragParts.join('#') : '';
  const rest = pathPart.slice(PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0) return null;                       // need both a group and a docKey
  const group = safeDecode(rest.slice(0, slash));
  const docKey = safeDecode(rest.slice(slash + 1)).replace(/\/+$/, '');
  if (!group || !docKey) return null;
  return { group, docKey, frag: safeDecode(frag) };
}

// The ancestor FOLDER docKeys of a document — literally its `/`-prefixes, excluding
// itself. The backend builds folder nodes by accumulating the same segments
// (doc_serve.cpp insert_doc), so this is exactly the set to expand to reveal a
// target — no tree traversal needed.
export function ancestorKeys(docKey) {
  if (typeof docKey !== 'string' || !docKey) return [];
  const segs = docKey.split('/').filter(Boolean);
  const out = [];
  let acc = '';
  for (let i = 0; i < segs.length - 1; i += 1) {
    acc = acc ? `${acc}/${segs[i]}` : segs[i];
    out.push(acc);
  }
  return out;
}

// 这个 href 是不是「让浏览器自己去开」的那一类？
//   · `/api/docs/asset`   —— 发布管线重写出来的授权资产（publish.mjs 的 makeLinkRewriter）
//   · `/api/docs/artifact` —— 文档引用的外部交付物（第一百一十八批；闸是文档可见性）
//   · `/api/me/repos/raw` —— 成员只读仓下载面（第一百三十四批；闸是 is_internal_party）
//
// ⚠ 第一百一十八批实撞：`artifact` 上线后，纪要里那个 web.zip 链接点下去弹的是
//   **「该链接指向文件夹，暂不支持跳转」**——因为它落到了兜底那一支。
//   症状与真的点了个文件夹链接**完全一样**，而病因毫无关系。
//   所以这里判的是**前缀家族**而不是逐个字符串：下一条 `/api/docs/*` 形态的
//   下载路由若再漏进来，仍会以「文件夹」的面貌出现，而那是最难查的一种。
// ⚠ 第一百三十四批**如约复发**：新下载面开在 `/api/me/repos/raw`——不在 `/api/docs/*`
//   家族里，于是上一段预言的形状原样上演（09 终审纪要的资源表整列点不动）。
//   判据从两个前缀改成「`/api/` 打头的一律交给浏览器」不行——`/api/docs/content`
//   等 JSON 端点也在 `/api/` 下，让浏览器直开会把 JSON 画在页面上。所以仍是
//   **显式家族清单**，但每开一条「浏览器直开」的下载路由，这里就是登记处
//  （api-conventions §3.11 的端点登记随裁提醒这一条）。
export function isAssetHref(href) {
  return typeof href === 'string'
    && (href.startsWith('/api/docs/asset') || href.startsWith('/api/docs/artifact')
        || href.startsWith('/api/me/repos/raw'));
}

// Exported since SB-042: DocBrowserPage carried its own copy (safeDecodeHash)
// of exactly this guard — one URI-decoder that never throws, shared instead.
export function safeDecode(s) {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}
